import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { Log } from './logger.service';

/**
 * Service for managing browser localStorage with type-safe operations.
 * Extends the abstract StorageService to provide localStorage-specific functionality.
 *
 * Data stored via this service persists across browser sessions and tabs
 * until explicitly removed or the user clears browser data.
 *
 * @class LocalStorageService
 * @extends {StorageService}
 *
 * @example
 * ```typescript
 * // Inject in component/service
 * constructor(private localStorage: LocalStorageService) {}
 *
 * // Store data
 * this.localStorage.setItem('user', { name: 'John', age: 30 });
 *
 * // Retrieve data
 * const user: User | null = this.localStorage.getItem<User>('user');
 *
 * // Method chaining
 * this.localStorage
 *   .setItem('theme', 'dark')
 *   .setItem('language', 'en');
 *
 * // Check existence
 * if (this.localStorage.hasItem('user')) {
 *   // User data exists
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class LocalStorageService extends StorageService {

  /**
   * Creates an instance of LocalStorageService.
   * Automatically configures the service to use browser's localStorage.
   */
  constructor() {
    super(localStorage, Log.create(LocalStorageService));
  }
}
